import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/services/supabase-client.service";
import { AuthContext } from "@/hooks/useAuth";
import { logger } from "@/lib/logger";
import {
    BillingSettingsData,
    NotificationSettingsData,
    SecuritySettingsData,
    DEFAULT_BILLING_SETTINGS,
    DEFAULT_NOTIFICATION_SETTINGS,
    DEFAULT_SECURITY_SETTINGS,
    getBillingSettings,
    getNotificationSettings,
    getSecuritySettings,
} from "@/services/settings.service";

interface SettingsState {
    billing: BillingSettingsData;
    notifications: NotificationSettingsData;
    security: SecuritySettingsData;
    loading: boolean;
    error: string | null;
}

interface SettingsContextValue extends SettingsState {
    refreshSettings: () => Promise<void>;
    updateBillingLocally: (update: Partial<BillingSettingsData>) => void;
    updateNotificationsLocally: (update: Partial<NotificationSettingsData>) => void;
    updateSecurityLocally: (update: Partial<SecuritySettingsData>) => void;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
    const auth = useContext(AuthContext);
    const practiceId = auth?.practiceId ?? null;
    const [state, setState] = useState<SettingsState>({
        billing: DEFAULT_BILLING_SETTINGS,
        notifications: DEFAULT_NOTIFICATION_SETTINGS,
        security: DEFAULT_SECURITY_SETTINGS,
        loading: true,
        error: null,
    });

    const loadSettings = async (id: string) => {
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const [billing, notifications, security] = await Promise.all([
                getBillingSettings(id),
                getNotificationSettings(id),
                getSecuritySettings(id),
            ]);
            setState({
                billing,
                notifications,
                security,
                loading: false,
                error: null,
            });
        } catch (err) {
            logger.error("[SettingsProvider] Failed to load settings:", err);
            setState((s) => ({
                ...s,
                loading: false,
                error: "Fehler beim Laden der Einstellungen.",
            }));
        }
    };

    useEffect(() => {
        if (!practiceId) {
            setState({
                billing: DEFAULT_BILLING_SETTINGS,
                notifications: DEFAULT_NOTIFICATION_SETTINGS,
                security: DEFAULT_SECURITY_SETTINGS,
                loading: false,
                error: null,
            });
            return;
        }

        let mounted = true;

        // Load initial data
        loadSettings(practiceId);

        // Setup Realtime subscriptions
        const billingSub = supabase
            .channel("public:billing_settings")
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "billing_settings",
                    filter: `practice_id=eq.${practiceId}`,
                },
                () => {
                    if (mounted) {
                        logger.info(
                            "[SettingsProvider] Billing settings changed remotely. Reloading.",
                        );
                        getBillingSettings(practiceId).then((billing) => {
                            if (mounted) setState((s) => ({ ...s, billing }));
                        });
                    }
                },
            )
            .subscribe();

        const practiceSettingsSub = supabase
            .channel("public:practice_settings")
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "practice_settings",
                    filter: `practice_id=eq.${practiceId}`,
                },
                () => {
                    if (mounted) {
                        logger.info(
                            "[SettingsProvider] Practice settings changed remotely. Reloading notifications/security.",
                        );
                        Promise.all([
                            getNotificationSettings(practiceId),
                            getSecuritySettings(practiceId),
                        ]).then(([notifications, security]) => {
                            if (mounted) setState((s) => ({ ...s, notifications, security }));
                        });
                    }
                },
            )
            .subscribe();

        return () => {
            mounted = false;
            billingSub.unsubscribe();
            practiceSettingsSub.unsubscribe();
        };
    }, [practiceId]);

    const value: SettingsContextValue = {
        ...state,
        refreshSettings: async () => {
            if (practiceId) await loadSettings(practiceId);
        },
        updateBillingLocally: (update) => {
            setState((s) => ({ ...s, billing: { ...s.billing, ...update } }));
        },
        updateNotificationsLocally: (update) => {
            setState((s) => ({ ...s, notifications: { ...s.notifications, ...update } }));
        },
        updateSecurityLocally: (update) => {
            setState((s) => ({ ...s, security: { ...s.security, ...update } }));
        },
    };

    return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings(): SettingsContextValue {
    const ctx = useContext(SettingsContext);
    if (!ctx) {
        throw new Error("useSettings must be used within a SettingsProvider");
    }
    return ctx;
}
